import React from 'react';
import './ReportSteps.css';

// Basic address-shape check: local@domain.tld. Catches typos/malformed input
// before save so the user gets an immediate error instead of a silently
// non-delivering report (PRD-1269). A well-formed but non-existent address
// still passes here — that surfaces as a delivery failure on the run record.
// Exported so the wizard can validate a typed-but-not-added address on "Next".
export const isValidReportEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

/**
 * The Output step, shared by the Reports and Alerts wizards.
 *
 * `title` exists so the Alerts wizard can reuse this step rather than clone it
 * — PRD-1248 specifies the Output step "unchanged in shape". The default is the
 * Reports wording, so the Reports flow is unchanged.
 *
 * The pending-address state lives in `formData` (PRD-1269) so a wizard's Next
 * handler can validate and commit a typed-but-not-added address. **Both**
 * wizards must do that, or the Alerts flow silently drops it.
 */
const ReportStepOutput = ({ formData, onSetFormData, title = 'Output' }) => {
  const emailToSelf = formData.reportEmailToSelf !== false;
  const recipients = formData.reportRecipients ?? [];
  // Pending (typed-but-not-yet-added) address + its error live in formData so the
  // wizard's Next handler can validate/commit it before advancing.
  const newEmail = formData.pendingRecipient ?? '';
  const emailError = formData.recipientError ?? '';

  const userEmail = formData.userEmail != null ? formData.userEmail : null;
  const displayEmail = userEmail && userEmail.trim() ? userEmail.trim() : 'Your account email';

  const handleEmailToSelfChange = (e) => {
    onSetFormData({
      ...formData,
      reportEmailToSelf: e.target.checked,
    });
  };

  const handleAddRecipient = () => {
    const trimmed = (newEmail || '').trim();
    if (!trimmed) return;
    if (!isValidReportEmail(trimmed)) {
      onSetFormData({ ...formData, recipientError: 'Enter a valid email address (e.g. name@example.com).' });
      return;
    }
    if (recipients.includes(trimmed)) {
      onSetFormData({ ...formData, recipientError: 'That address is already added.' });
      return;
    }
    onSetFormData({
      ...formData,
      reportRecipients: [...recipients, trimmed],
      pendingRecipient: '',
      recipientError: '',
    });
  };

  const handleRemoveRecipient = (email) => {
    onSetFormData({
      ...formData,
      reportRecipients: recipients.filter((e) => e !== email),
    });
  };

  return (
    <div className="report-step-content">
      <h3 className="report-step-title">{title}</h3>

      <div className="report-form-field report-checkbox-field">
        <label className="report-checkbox-label resui-checkbox">
          <input
            type="checkbox"
            checked={emailToSelf}
            onChange={handleEmailToSelfChange}
            className="report-checkbox resui-checkbox-input"
          />
          <span className="resui-checkbox-mark" />
          <span className="report-label-inline resui-checkbox-label">Email to self</span>
        </label>
        <p className="report-helper">Uses your account email: {displayEmail}</p>
      </div>

      <div className="report-form-field">
        <label className="report-label">Email To Others</label>
        <div className="report-add-recipient-row">
          <input
            type="email"
            className="resui-input report-email-input"
            placeholder="Add email address"
            value={newEmail}
            onChange={(e) => onSetFormData({ ...formData, pendingRecipient: e.target.value, recipientError: '' })}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRecipient())}
            aria-invalid={emailError ? 'true' : 'false'}
          />
          <button
            type="button"
            className="report-button report-button-add-recipient"
            onClick={handleAddRecipient}
          >
            ADD RECIPIENT
          </button>
        </div>
        {emailError && <p className="report-email-error" role="alert">{emailError}</p>}
        {recipients.length > 0 && (
          <ul className="report-recipients-list">
            {recipients.map((email) => (
              <li key={email} className="report-recipient-item">
                <span>{email}</span>
                <button
                  type="button"
                  className="report-remove-recipient"
                  onClick={() => handleRemoveRecipient(email)}
                  aria-label={`Remove ${email}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ReportStepOutput;
